import React, { useEffect, useState } from 'react';
import { X, Send, AlertCircle, Mail } from 'lucide-react';
import { api } from '../lib/api';
import { useToast } from '../hooks/use-toast';

/**
 * ReplyDialog — compose an email reply from within the admin dashboard.
 * Props:
 *  - open: bool
 *  - onClose()
 *  - onSent()  // called after a successful send
 *  - to: recipient email
 *  - originalSubject: subject of original (will be prefixed with "Re: ")
 *  - originalMessage: string of original message (quoted in reply)
 *  - originalName: name of sender (used in greeting)
 *  - referenceType: 'contact' | 'prayer'
 *  - referenceId: string
 *  - smtpConfigured: bool (from parent)
 */
const ReplyDialog = ({
  open, onClose, onSent,
  to, originalSubject, originalMessage, originalName,
  referenceType, referenceId,
  smtpConfigured,
}) => {
  const { toast } = useToast();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setSubject(originalSubject?.startsWith('Re:') ? originalSubject : `Re: ${originalSubject || ''}`.trim());
    const greeting = originalName?.trim() ? `Dear ${originalName.trim()},` : 'Hello,';
    const quoted = originalMessage
      ? `\n\n\n---\nOn your previous message, you wrote:\n> ${originalMessage.split('\n').join('\n> ')}`
      : '';
    setBody(`${greeting}\n\nThank you for reaching out to All Saints Catholic Parish.\n\n\n\nIn Christ,\nThe Parish Office${quoted}`);
  }, [open, originalSubject, originalMessage, originalName]);

  if (!open) return null;

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await api.sendReply({
        to,
        subject,
        body,
        reference_type: referenceType,
        reference_id: referenceId,
      });
      toast({ title: 'Reply sent', description: `Delivered to ${to}` });
      onSent && onSent();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send reply.';
      setError(detail);
    } finally {
      setSending(false);
    }
  };

  const openInMail = () => {
    const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white border border-[#e9e0cc] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9e0cc]">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-[#3a2359]" />
            <h3 className="font-display text-2xl text-[#2a1a45]">Reply from Inbox</h3>
          </div>
          <button onClick={onClose} className="text-[#5a534a] hover:text-[#3a2359]"><X size={20} /></button>
        </div>

        {!smtpConfigured && (
          <div className="mx-6 mt-5 flex items-start gap-2 text-sm text-[#7a5a1c] bg-[#fff9e6] border border-[#e6d896] p-3">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium">Email delivery not configured.</span>
              <span> You can still compose & open in your mail app, or ask an administrator to add SMTP credentials to the backend .env.</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSend} className="px-6 py-5 space-y-4">
          {error && <div className="text-sm text-[#7a1c1c] bg-[#fdecec] border border-[#f0c4c4] p-3">{error}</div>}

          <div>
            <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">To</label>
            <input
              type="email"
              value={to}
              readOnly
              className="w-full px-4 py-2.5 border border-[#e9e0cc] bg-[#faf7f0]/60 text-sm text-[#78716c]"
            />
          </div>

          <div>
            <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Subject</label>
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              required
              className="w-full px-4 py-2.5 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm"
            />
          </div>

          <div>
            <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Message</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              required
              rows={12}
              className="w-full px-4 py-2.5 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm resize-none font-body leading-relaxed"
            />
          </div>

          <div className="flex items-center justify-between gap-2 pt-3 border-t border-[#e9e0cc]">
            <button type="button" onClick={openInMail} className="text-sm text-[#3a2359] hover:text-[#4d2f75] inline-flex items-center gap-1.5">
              <Mail size={14} /> Open in mail app
            </button>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-[#e9e0cc] text-sm text-[#5a534a] hover:bg-[#faf7f0]">Cancel</button>
              <button
                type="submit"
                disabled={sending || !smtpConfigured}
                title={!smtpConfigured ? 'SMTP not configured — use "Open in mail app" instead' : 'Send reply'}
                className="inline-flex items-center gap-2 px-5 py-2 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={14} /> {sending ? 'Sending…' : 'Send Reply'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReplyDialog;
